import type { ReactNode } from "react";
import { agruparPorCategoria } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { CatalogItemRow } from "./CatalogItemRow";

export type ServicoDoCatalogo = { id: string; label: string; group: string | null; active: boolean };

/**
 * Os serviços do laboratório em cartões por categoria, sempre abertos (pedido do Jun). Quem chama
 * passa só o que o laboratório oferece — a base inteira fica no "Adicionar serviço" — então os
 * cartões são curtos e não precisam recolher. O cabeçalho liga/desliga o que está no cartão.
 */
export function ServicosPorCategoria<T extends ServicoDoCatalogo>({
  servicos,
  vazio,
  renderItem,
  onToggleCategoria,
  ocupado,
  umPorLinha = false,
}: {
  servicos: T[];
  /** O que mostrar sem nenhum serviço (catálogo vazio ou busca sem resultado). */
  vazio: ReactNode;
  renderItem: (servico: T) => ReactNode;
  /** `itens`: os serviços do cartão — ligar volta só esses, nunca a categoria inteira da base. */
  onToggleCategoria: (categoria: string, ligar: boolean, itens: T[]) => void;
  ocupado?: boolean;
  /** Um cartão por linha, com os serviços em duas colunas dentro (Catálogo). */
  umPorLinha?: boolean;
}) {
  const grupos = agruparPorCategoria(servicos);
  if (grupos.length === 0) return <>{vazio}</>;

  return (
    <div className={cn("grid grid-cols-1 items-start gap-3", !umPorLinha && "md:grid-cols-2")}>
      {grupos.map(({ categoria, itens }) => {
        const ativos = itens.filter((s) => s.active).length;
        const agregado: boolean | "indeterminate" =
          ativos === 0 ? false : ativos === itens.length ? true : "indeterminate";
        return (
          <div
            key={categoria}
            className={cn(
              "rounded-lg border p-3",
              umPorLinha ? "border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card" : "border-zinc-100 dark:border-border/60",
            )}
          >
            <CatalogItemRow
              label={categoria}
              destaque
              sublabel={`${ativos} ${ativos === 1 ? "serviço ativo" : "serviços ativos"}`}
              enabled={agregado}
              disabled={ocupado}
              onToggle={(ligar) => onToggleCategoria(categoria, ligar, itens)}
            />
            <div className={cn("mt-1 border-t border-zinc-100 pt-1 dark:border-border/60", umPorLinha && "grid items-start gap-x-6 md:grid-cols-2")}>
              {itens.map(renderItem)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
